import {getUUID} from '@yandex-delivery/utils'
import {useCallback, useRef, useState} from 'react'
import {useController} from 'react-hook-form'

import {useAddresses} from '../../actions/RouteMap/lib/useAddresses'
import {FieldName} from '../../lib/fieldNames'
import type {Contact, Route} from '../../types'
import type {AddressWithContacts} from './RouteInfo.types'

export const useRouteInfo = ({onSave}: {onSave?: () => void}) => {
  const {
    field: {value: defaultRouteFieldValue, onChange: updateRoute},
  } = useController({name: FieldName.Route})
  const {
    field: {value: defaultContactsValue, onChange: updateContacts},
  } = useController({name: FieldName.Contacts})

  const addresses = useAddresses(defaultRouteFieldValue ?? {})

  const [addressesWithContacts, setAddressesWithContacts] = useState<AddressWithContacts[]>(() =>
    addresses.map((address, index) => ({
      address,
      contacts: {
        ...(defaultContactsValue ?? []).find(
          (contact: Contact) => contact.pointId === (index === 0 ? 'source' : `destination_${index}`),
        ),
      },
      id: getUUID(),
    })),
  )

  const routeInfoRef = useRef<HTMLElement>(null)

  const handleSave = useCallback(() => {
    let newRoute: Route = {}
    const newContacts: Contact[] = []
    addressesWithContacts.forEach(({address, contacts}, index) => {
      const key = index === 0 ? 'source' : `destination_${index}`
      contacts.pointId = key
      newRoute[key] = {address}
      newContacts.push(contacts)
    })

    // Обновляем только destination точки
    const prevRouteCopy = {...defaultRouteFieldValue}
    for (const key of Object.keys(prevRouteCopy)) {
      if (key.startsWith('destination_')) {
        delete prevRouteCopy[key]
      }
    }

    newRoute = {...prevRouteCopy, ...newRoute}
    updateContacts(newContacts)
    updateRoute(newRoute)

    onSave?.()
  }, [addressesWithContacts, defaultRouteFieldValue, onSave, updateContacts, updateRoute])

  return {
    addresses: addressesWithContacts,
    setAddresses: setAddressesWithContacts,
    routeInfoRef,
    handleSave,
  }
}
